<template>
  <div class="wrapper fair-app">
    <RouterView />

    <!-- FE-024 — Figma `Bottomnav bar` 11:39225: the one mobile bottom bar, every route below 1024.
         The legacy PrimeVue Dock (`features/bottom-app-bar`) is gone. -->
    <FairBottomNavController v-if="showFairBottomNav" />

    <!-- FE-034 — cookie consent banner (Figma `Cookies` 39:41637), until a choice is stored. -->
    <FairCookieBanner v-if="isClient" />

    <TasteClarificationModalWrapper />
    <SurveyModalWrapper />
    <GiftModalWrapper />
    <LookModalWrapper />

    <AuthWrapper />

    <AiraFloatingWidget v-if="showAiraWidget" />
    <AiraContextualCard v-if="showAiraWidget" />
    <AiraChatDrawer v-if="showAiraWidget" />
    <AiraDevPanel v-if="showAiraDevPanel" />
    <AiraBlackoutOverlay v-if="isClient" />

    <FairPageLoader />
    <FairToastHost />
  </div>
</template>

<script setup lang="ts">
import { RouterView, useRoute } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";
import { AiraFloatingWidget, AiraDevPanel, AiraBlackoutOverlay } from "@/features/aira-widget";
import { AiraContextualCard } from "@/features/aira/contextual";
import AiraChatDrawer from "@/widgets/aira/AiraChatDrawer.vue";
import AuthWrapper from "@/features/auth/ui/modals/AuthWrapper.vue";
import SurveyModalWrapper from "@/features/survey/ui/modals/SurveyModalWrapper.vue";
import TasteClarificationModalWrapper from "@/features/taste/ui/modals/TasteClarificationModalWrapper.vue";
import GiftModalWrapper from "@/features/gift/ui/modals/GiftModalWrapper.vue";
import LookModalWrapper from "@/features/look/ui/modals/LookModalWrapper.vue";
import { FairBottomNavController } from "@/features/bottom-nav";
import { FairCookieBanner } from "@/features/cookie-consent";
import { useDevice } from "@/composables/useDevice";
import { useTokenStore } from "@/features/auth/store";
import { useTasteClarificationModal } from "@/features/taste/store";
import { useSurveyModal } from "@/features/survey/store";
import { useLookModal } from "@/features/look/store";
import { useGiftModal } from "@/features/gift/store";
import { FairPageLoader } from "@/ui/patterns/FairPageLoader";
import { FairToastHost } from "@/ui/patterns/FairToast";

const { isDevice } = useDevice();
const route = useRoute();
const deviceType = computed(() => isDevice.value);

/** Rive needs a real canvas — render the widget after client mount, never during SSG. */
const isClient = ref(false);
onMounted(() => {
  isClient.value = true;
});

/** The ball is hidden where AIRA already has a surface: the chat page and the playground canvas. */
const showAiraWidget = computed(
  () =>
    isClient.value &&
    route.name !== "chatbot" &&
    route.name !== "style-space-playground",
);

/** FE-024 — the mobile bottom nav bar: every route below 1024 except the AIRA chat itself. */
const showFairBottomNav = computed(() => {
  if (!isClient.value) return false;
  const isCompactDevice =
    deviceType.value === "mobile" || deviceType.value === "tablet";
  return isCompactDevice && route.name !== "chatbot";
});

/** Manual state toggles for Aira, dev builds only, opened with ?aira=debug. */
const showAiraDevPanel = computed(
  () => import.meta.env.DEV && showAiraWidget.value && route.query.aira === "debug",
);

// Security: close all modals when user is logged out (token cleared)
const tokenStore = useTokenStore();
watch(() => tokenStore.accessToken, (newToken, oldToken) => {
  if (oldToken && !newToken) {
    // Token was cleared — user logged out or session expired
    const tasteStore = useTasteClarificationModal();
    const surveyStore = useSurveyModal();
    const lookStore = useLookModal();
    const giftStore = useGiftModal();

    if (tasteStore.tasteModal.isShow) tasteStore.tasteModal.isShow = false;
    if (surveyStore.surveyModal?.isShow) surveyStore.surveyModal.isShow = false;
    if (lookStore.lookModal?.isShow) lookStore.lookModal.isShow = false;
    if (giftStore.giftModal?.isShow) giftStore.giftModal.isShow = false;
  }
});
</script>

<style scoped>
.wrapper {
  display: flex;
  justify-content: center;
}
</style>
